import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Minus, Plus } from 'lucide-react';
import { clampOrderQuantity, MAX_ORDER_QUANTITY, MIN_ORDER_QUANTITY } from '../../config/orderQuantity';

interface QuantitySelectorProps {
  selectedQuantity: number;
  setSelectedQuantity: (qty: number) => void;
  quantities: number[];
}

export const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  selectedQuantity,
  setSelectedQuantity,
  quantities,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  // Calculate popover position
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + 6,
        left: rect.left,
      });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const handleDecrement = () => {
    setSelectedQuantity(clampOrderQuantity(selectedQuantity - 1, selectedQuantity));
  };

  const handleIncrement = () => {
    setSelectedQuantity(clampOrderQuantity(selectedQuantity + 1, selectedQuantity));
  };

  return (
    <>
      <button
        ref={buttonRef}
        className="trading-qty-btn"
        onClick={() => setIsOpen(!isOpen)}
        title="Order Quantity"
      >
        <span>Qty:</span>
        <span className="qty-value">{selectedQuantity}</span>
        <ChevronDown size={10} />
      </button>

      {isOpen && createPortal(
        <>
          <div className="trading-popover-overlay" onClick={() => setIsOpen(false)} />
          <div
            ref={popoverRef}
            className="trading-qty-popover"
            style={{
              top: popoverPosition.top,
              left: popoverPosition.left,
            }}
          >
            <div className="trading-qty-popover-header">
              <span className="trading-qty-popover-title">Quantity</span>
              <span className="trading-qty-popover-value">{selectedQuantity}</span>
            </div>

            <div className="trading-qty-row">
              <button className="trading-qty-adjust-btn" onClick={handleDecrement}>
                <Minus size={12} />
              </button>
              <input
                type="number"
                className="trading-qty-input"
                value={selectedQuantity}
                onChange={(e) => setSelectedQuantity(clampOrderQuantity(e.target.value, selectedQuantity))}
                min={MIN_ORDER_QUANTITY}
                max={MAX_ORDER_QUANTITY}
              />
              <button className="trading-qty-adjust-btn" onClick={handleIncrement}>
                <Plus size={12} />
              </button>
            </div>

            <div className="trading-qty-chips">
              {quantities.map((q) => (
                <button
                  key={q}
                  className={`trading-qty-chip ${selectedQuantity === q ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedQuantity(q);
                    setIsOpen(false);
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};
